const SCROLL_KEY_PREFIX = "load-more-scroll:";
let restoreAttempted = false;

class LoadMore extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector("[data-load-more-button]");
    this.countEl = this.querySelector("[data-load-more-count]");
    this.total = Number(this.dataset.total);
    this.selectors = (this.dataset.listSelector || "")
      .split(",")
      .map((selector) => selector.trim())
      .filter(Boolean);

    if (this.button) {
      this.button.addEventListener("click", this.onClick.bind(this));
    }

    this.setupScrollPersistence();
    this.restoreFromHistory();
  }

  get section() {
    return this.closest(".shopify-section");
  }

  get scrollKey() {
    return SCROLL_KEY_PREFIX + window.location.pathname + window.location.search;
  }

  async onClick() {
    if (this.loading || this.button.disabled) return;

    const nextUrl = this.button.dataset.nextUrl;
    if (!nextUrl) return;

    this.setLoading(true);

    try {
      const url = new URL(nextUrl, window.location.origin);
      url.searchParams.set("section_id", this.dataset.sectionId);

      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), "text/html");
      this.appendItems(doc);
      this.updateNextUrl(doc);
      this.updateStatus();
      this.syncHash(url);
    } catch (error) {
      console.error("[load-more]", error);
    } finally {
      this.setLoading(false);
    }
  }

  appendItems(doc) {
    this.selectors.forEach((selector) => {
      const target = this.section?.querySelector(selector);
      const incoming = doc.querySelector(selector);
      if (!target || !incoming) return;

      [...incoming.children].forEach((item) => {
        item.removeAttribute("reveal-on-scroll");
        target.appendChild(document.importNode(item, true));
      });
    });
  }

  updateStatus() {
    if (!this.countEl || this.selectors.length === 0) return;

    const primary = this.section?.querySelector(this.selectors[0]);
    if (!primary) return;

    const isLastPage = !this.button.dataset.nextUrl;
    const template = isLastPage ? this.countEl.dataset.allTemplate : this.countEl.dataset.countTemplate;

    this.countEl.textContent = template
      .replace("__START__", "1")
      .replace("__END__", primary.children.length)
      .replace("__TOTAL__", this.total);
  }

  updateNextUrl(doc) {
    const nextUrl = doc.querySelector("[data-load-more-button]")?.dataset.nextUrl;

    if (nextUrl) {
      this.button.dataset.nextUrl = nextUrl;
    } else {
      this.button.dataset.nextUrl = "";
      this.button.hidden = true;
    }
  }

  setLoading(isLoading) {
    this.loading = isLoading;

    if (isLoading) {
      this.button.setAttribute("aria-busy", "true");
      this.button.disabled = true;
    } else {
      this.button.removeAttribute("aria-busy");
      if (this.button.dataset.nextUrl) this.button.disabled = false;
    }
  }

  syncHash(fetchedUrl) {
    const page = fetchedUrl.searchParams.get("page");
    if (page) window.history.replaceState(null, "", `#page=${page}`);
  }

  pageFromHash() {
    const match = window.location.hash.match(/^#page=(\d+)$/);
    return match ? parseInt(match[1], 10) : 1;
  }

  setupScrollPersistence() {
    if (LoadMore.scrollPersistenceReady) return;
    LoadMore.scrollPersistenceReady = true;

    window.addEventListener("pagehide", () => {
      try {
        sessionStorage.setItem(this.scrollKey, String(window.scrollY));
      } catch (error) {
        // sessionStorage unavailable (e.g. private browsing)
      }
    });
  }

  restoreScroll() {
    const saved = sessionStorage.getItem(this.scrollKey);
    if (saved === null) return;

    window.scrollTo(0, parseInt(saved, 10));
    sessionStorage.removeItem(this.scrollKey);
  }

  async restoreFromHistory() {
    if (restoreAttempted) return;
    restoreAttempted = true;

    const navType = performance.getEntriesByType("navigation")[0]?.type;
    if (navType !== "back_forward") return;

    const targetPage = this.pageFromHash();
    if (targetPage <= 1) return;

    let lastDoc = null;
    for (let page = 2; page <= targetPage; page++) {
      const url = new URL(window.location.href);
      url.hash = "";
      url.searchParams.set("page", String(page));
      url.searchParams.set("section_id", this.dataset.sectionId);

      try {
        const response = await fetch(url.toString());
        if (!response.ok) break;

        lastDoc = new DOMParser().parseFromString(await response.text(), "text/html");
        this.appendItems(lastDoc);
      } catch (error) {
        console.error("[load-more] restore", error);
        break;
      }
    }

    if (lastDoc) {
      this.updateNextUrl(lastDoc);
      this.updateStatus();
    }

    this.restoreScroll();
  }
}

if (!window.customElements.get("load-more")) {
  window.customElements.define("load-more", LoadMore);
}
